import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';

interface DateRangeSelectorProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate,
  maxDate
}) => {
  const { t } = useTranslation();

  // Initialize with default dates if not set
  useEffect(() => {
    if (!startDate && minDate) {
      onStartDateChange(minDate);
    }
    if (!endDate && maxDate) {
      onEndDateChange(maxDate);
    }
  }, [startDate, endDate, minDate, maxDate, onStartDateChange, onEndDateChange]);

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse date from input
  const parseDateFromInput = (value: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const minDateStr = useMemo(() => minDate ? formatDateForInput(minDate) : undefined, [minDate]);
  const maxDateStr = useMemo(() => maxDate ? formatDateForInput(maxDate) : undefined, [maxDate]);

  return (
    <div className="flex max-lg:flex-wrap items-center gap-2 px-3 py-1.5 rounded-lg bg-white dark:bg-card-dark shadow">
      <Calendar className="w-4 h-4 text-zinc-600 dark:text-zinc-400" />
      <div className="flex max-lg:flex-wrap items-center gap-2">
        <label className="text-xs text-zinc-600 dark:text-zinc-400">
          {t('dateRange.from')}:
        </label>
        <input
          type="date"
          value={formatDateForInput(startDate)}
          onChange={(e) => onStartDateChange(parseDateFromInput(e.target.value))}
          min={minDateStr}
          max={endDate ? formatDateForInput(endDate) : maxDateStr}
          className="text-sm px-2 py-1 rounded border border-border dark:border-border-dark bg-transparent dark:bg-transparent text-zinc-900 dark:text-zinc-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
        />
        <label className="text-xs text-zinc-600 dark:text-zinc-400">
          {t('dateRange.to')}:
        </label>
        <input
          type="date"
          value={formatDateForInput(endDate)}
          onChange={(e) => onEndDateChange(parseDateFromInput(e.target.value))}
          min={startDate ? formatDateForInput(startDate) : minDateStr}
          max={maxDateStr}
          className="text-sm px-2 py-1 rounded border border-border dark:border-border-dark bg-transparent dark:bg-transparent text-zinc-900 dark:text-zinc-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
        />
      </div>
    </div>
  );
};

export default DateRangeSelector;
